// frontend/src/pages/builder/FieldList.jsx
// The builder's field picker: the dataset's chartable fields grouped by role
// (Time, Dimensions, Measures, Identifiers), with search. Clicking a field
// calls onAdd(field); dragging it onto a slot drops it there (Wells.jsx).
// Each field shows its type: Aa text, # number, Dt date, T/F yes-no, Σ a saved
// measure. Fields already in the chart are shown on the inset shade (7c-2).
// 13c-3: calculated columns carry an "fx" tag and their description; saved
// measures sit in their own group and show their formula's description.
import React, { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { PanelLabel } from '@/components/ui/panel.jsx';
import { groupFields, typeMarker } from './builderSpec';
import { FIELD_MIME } from './Wells';

export default function FieldList({ fields, onAdd, usedKeys = [], disabled = false }) {
  const [query, setQuery] = useState('');
  const groups = useMemo(() => groupFields(fields, query), [fields, query]);
  const used = useMemo(() => new Set(usedKeys), [usedKeys]);

  return (
    <div className="flex flex-col">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--ui-text-muted)]" aria-hidden="true" />
        <input
          type="search" value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Search fields" aria-label="Search fields"
          className="h-9 w-full rounded-xl bg-[var(--ui-inset)] pl-9 pr-3 text-[13px] text-[var(--ui-text)] placeholder:text-[var(--ui-text-muted)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
        />
      </div>

      {groups.length === 0 && (
        <p className="mt-3 text-xs text-[var(--ui-text-muted)]">{query ? 'No fields match.' : 'This dataset has no chartable fields.'}</p>
      )}

      <div className="flex max-h-[520px] flex-col overflow-y-auto scrollbar-thin pr-1">
        {groups.map((g) => (
          <div key={g.role}>
            <PanelLabel className="first:mt-3">{g.label}</PanelLabel>
            <ul className="flex flex-col gap-px">
              {g.fields.map((f) => {
                const inChart = used.has(f.key);
                return (
                  <li key={f.key}>
                    <button
                      type="button" disabled={disabled} onClick={() => onAdd(f)}
                      draggable={!disabled}
                      onDragStart={(e) => { e.dataTransfer.setData(FIELD_MIME, f.key); e.dataTransfer.effectAllowed = 'copy'; }}
                      title={f.description || (f.sample_value ? `Example: ${f.sample_value}` : f.key)}
                      className={`flex w-full cursor-grab items-center gap-2.5 rounded-[10px] px-2.5 py-[7px] text-left text-[13.5px] text-[var(--ui-text)] transition-colors hover:bg-[var(--ui-inset-hover)] active:cursor-grabbing disabled:opacity-50 ${inChart ? 'bg-[var(--ui-inset)]' : ''}`}
                    >
                      <span className="w-[18px] shrink-0 font-mono text-[10px] text-[var(--ui-text-muted)]" aria-hidden="true">{typeMarker(f)}</span>
                      <span className="truncate">{f.display_name}</span>
                      {f.is_custom && <span className="ml-auto shrink-0 text-[10px] text-[var(--ui-text-muted)]">custom</span>}
                      {f.calculated && <span className="ml-auto shrink-0 font-mono text-[10px] italic text-[var(--ui-text-muted)]" title="A calculated column">fx</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
